import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, unref as _unref, createVNode as _createVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-0cc38f86"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "team-color-picker" }
const _hoisted_2 = ["onClick"]
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "picker-label" }, "Team", -1))
const _hoisted_4 = { class: "picker-name" }

import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { RefreshCwIcon } from '@zhuowenli/vue-feather-icons'
import * as sounds from './matching/GameSounds'

const stackedTransform = 'translate(-50%, -50%) translate(0px, 0px) scale(0)'

// the team's 8-colour set persists across open/close so reopening shows the same options (and the
// currently-selected colour stays among them); only the die reroll replaces it

export default /*#__PURE__*/_defineComponent({
  __name: 'TeamColorPicker',
  props: {
    color: {},
    name: {},
    open: { type: Boolean },
    hintDelay: {}
  },
  emits: ["toggle", "update:color", "update:name"],
  setup(__props: any, { emit }) {

const props = __props;




// the 8 alternate swatch positions around the centre (px offsets)
const altSlots = [
  { dx: -60, dy: -60 }, { dx: 0, dy: -60 }, { dx: 60, dy: -60 },
  { dx: -60, dy: 0 }, { dx: 60, dy: 0 },
  { dx: -60, dy: 60 }, { dx: 0, dy: 60 }, { dx: 60, dy: 60 },
]
// same transform-function shape as the slot transform, so scale(0) interpolates cleanly
const colorSetRef = ref<{ color: string; name: string }[]>([])
const altsRef = ref<{ color: string; name: string; transform: string }[]>([])
const rollingRef = ref(false)

// the picker is "open" when the player opened it (prop) or the one-time affordance hint is running;
// both drive the same shrink-and-fan-out animation
const hintOpenRef = ref(false)
const isOpenRef = computed(() => props.open || hintOpenRef.value)

function hslToHex(h: number, s: number, l: number) {
  s /= 100
  l /= 100
  const a = s * Math.min(l, 1 - l)
  const channel = (n: number) => {
    const k = (n + h / 30) % 12
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))
    return Math.round(c * 255).toString(16).padStart(2, '0')
  }
  return `#${channel(0)}${channel(8)}${channel(4)}`
}

// wide but non-extreme colours: never washed-out grey or blown-out neon/near-white
function randomColor() {
  const h = Math.floor(Math.random() * 360)
  const s = 10 + Math.floor(Math.random() * 85)
  const l = 10 + Math.floor(Math.random() * 85)
  return hslToHex(h, s, l)
}

async function fetchColorNames(colors: string[]) {
  try {
    const values = colors.map(c => c.replace('#', '')).join(',')
    const response = await fetch(`https://api.color.pizza/v1/?values=${values}&list=bestOf`)
    const data = await response.json()
    return colors.map((c, i) => data.colors?.[i]?.name ?? c)
  } catch {
    return colors
  }
}

function slotTransform(i: number) {
  return `translate(-50%, -50%) translate(${altSlots[i].dx}px, ${altSlots[i].dy}px) scale(1)`
}

function makeColorSet() {
  const colors = Array.from({ length: 8 }, () => randomColor())
  colorSetRef.value = colors.map(color => ({ color, name: color }))
  fetchColorNames(colors).then(names => {
    colorSetRef.value = colorSetRef.value.map((s, i) => ({ ...s, name: names[i] }))
  })
}

// fly the (persistent) colour set out from behind the centre
function showAlts() {
  if (colorSetRef.value.length === 0) {
    makeColorSet()
  }
  // render stacked behind the centre, then (after a paint) fly out to the slots
  altsRef.value = colorSetRef.value.map(s => ({ ...s, transform: stackedTransform }))
  requestAnimationFrame(() => requestAnimationFrame(() => {
    altsRef.value = altsRef.value.map((s, i) => ({ ...s, transform: slotTransform(i) }))
  }))
}

watch(isOpenRef, (isOpen) => {
  if (isOpen) {
    showAlts()
  } else {
    altsRef.value = altsRef.value.map(s => ({ ...s, transform: stackedTransform }))
    setTimeout(() => { altsRef.value = [] }, 350)
  }
})

// one-time affordance hint: soon after the picker appears, run the normal open→close animation
// (centre circle shrinks, colours fan out, then back in) so players notice it's interactive. Driven
// through hintOpenRef so it reuses the exact open/close behaviour -- no sound, since sounds only fire
// on real taps. Skipped if the player opens the picker first. Both teams' pickers mount together, so
// the hints run in sync.
let hintOutTimer = 0
let hintInTimer = 0

onMounted(() => {
  // hintDelay staggers the two teams so they don't fan out in perfect lockstep
  const out = 500 + (props.hintDelay ?? 0)
  hintOutTimer = window.setTimeout(() => { if (!props.open) hintOpenRef.value = true }, out)
  hintInTimer = window.setTimeout(() => { hintOpenRef.value = false }, out + 700)
})

onUnmounted(() => {
  window.clearTimeout(hintOutTimer)
  window.clearTimeout(hintInTimer)
})

// keep the rendered swatches in sync when the colour names resolve after the set is shown
watch(colorSetRef, (set) => {
  if (altsRef.value.length === set.length) {
    altsRef.value = altsRef.value.map((s, i) => ({ ...s, color: set[i].color, name: set[i].name }))
  }
})

function toggleWithSound() {
  if (props.open) {
    sounds.collapse()
  } else {
    sounds.expand()
  }
  emit('toggle')
}

// tapping a swatch simply sets the team's colour and name — no swapping
function pick(i: number) {
  sounds.colorChange()
  const chosen = colorSetRef.value[i]
  emit('update:color', chosen.color)
  emit('update:name', chosen.name)
}

async function roll() {
  if (rollingRef.value) {
    return
  }
  rollingRef.value = true

  sounds.collapse()
  altsRef.value = altsRef.value.map(s => ({ ...s, transform: stackedTransform }))

  const colors = Array.from({ length: 8 }, () => randomColor())
  const [names] = await Promise.all([fetchColorNames(colors), new Promise(resolve => setTimeout(resolve, 400))])

  altsRef.value = []
  colorSetRef.value = colors.map((color, i) => ({ color, name: names[i] }))
  emit('update:color', colors[0])
  emit('update:name', names[0])

  await new Promise(resolve => setTimeout(resolve, 50))
  sounds.expand()
  showAlts()

  rollingRef.value = false
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass(["swatch-picker", { open: isOpenRef.value }])
    }, [
      _createElementVNode("button", {
        class: "swatch center",
        style: _normalizeStyle({ background: _ctx.color }),
        onClick: toggleWithSound
      }, null, 4),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(altsRef.value, (sw, i) => {
        return (_openBlock(), _createElementBlock("button", {
          key: i,
          class: "swatch alt",
          style: _normalizeStyle({ background: sw.color, transform: sw.transform }),
          onClick: ($event: any) => (pick(i))
        }, null, 12, _hoisted_2))
      }), 128))
    ], 2),
    _hoisted_3,
    _createElementVNode("div", _hoisted_4, _toDisplayString(_ctx.name), 1),
    _createElementVNode("button", {
      class: _normalizeClass(["picker-die", { rolling: rollingRef.value, hidden: !_ctx.open }]),
      onClick: roll
    }, [
      _createVNode(_unref(RefreshCwIcon))
    ], 2)
  ]))
}
}

})